import React, { createContext, useContext, useState, useEffect } from "react";
import api from "../utils/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Note: Axios configuration is now handled in utils/api.js

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await api.get("/api/auth/profile");
          setUser(response.data.user);
          setIsAuthenticated(true);
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("token");
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await api.post("/api/auth/login", {
        username,
        password,
      });

      const { token, user } = response.data;

      if (!token) {
        console.error("No token received in response");
        return {
          success: false,
          error: "No authentication token received",
        };
      }

      localStorage.setItem("token", token);

      setUser(user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      // Provide more specific error messages
      let errorMessage = "Login failed";

      if (error.response) {
        // Server responded with error
        errorMessage =
          error.response.data?.error ||
          error.response.data?.message ||
          `Login failed: ${error.response.status} ${error.response.statusText}`;
      } else if (error.request) {
        // Request was made but no response received
        errorMessage =
          "Unable to connect to server. Please check your connection.";
      } else {
        // Something else happened
        errorMessage = error.message || "An unexpected error occurred";
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const loginWithToken = async (azureToken) => {
    setLoading(true);
    try {
      const response = await api.post("/api/auth/login/token", {
        token: azureToken,
      });

      const { code, token } = response.data;

      if (code !== 200 || !token) {
        return { success: false, message: "No authentication token received" };
      }

      localStorage.setItem("token", token);

      // Fetch user profile to get user details
      try {
        const profileResponse = await api.get("/api/auth/profile");
        setUser(profileResponse.data.user);
        setIsAuthenticated(true);
      } catch (profileError) {
        // Still set authenticated even if profile fetch fails
        setIsAuthenticated(true);
      }

      return { success: true };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Sign in with Microsoft failed. Please try again.";
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/api/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.put("/api/auth/change-password", {
        currentPassword,
        newPassword,
      });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Failed to change password",
      };
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    loginWithToken,
    logout,
    changePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
